import { useState, useMemo } from "react";
import { Filter, X } from "lucide-react";
import { Column } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Device } from "@/hooks/useDevices";

import { MappedTagMinimal } from "@/utils/tagMapping";
type Tag = MappedTagMinimal;

interface ColumnTagFilterProps {
  column: Column<Device, unknown>;
  availableTags: Tag[];
}

export function ColumnTagFilter({ column, availableTags }: ColumnTagFilterProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const filterValue = column.getFilterValue() as string[] | undefined;
  const selectedTagIds = filterValue || [];
  const isFiltered = selectedTagIds.length > 0;
  
  // Filter tags based on search query
  const filteredTags = useMemo(() => {
    if (!searchQuery) return availableTags;
    return availableTags.filter(tag => 
      (tag.name || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableTags, searchQuery]);
  
  // Toggle tag selection
  const handleTagToggle = (tagId: string, checked: boolean) => {
    const newTagIds = checked
      ? [...selectedTagIds, tagId]
      : selectedTagIds.filter(id => id !== tagId);
    
    column.setFilterValue(newTagIds.length > 0 ? newTagIds : undefined);
  };
  
  // Clear all selections
  const clearFilter = () => {
    column.setFilterValue(undefined);
    setSearchQuery("");
  };
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn(
            "h-8 w-8",
            isFiltered && "text-primary"
          )}
        >
          <Filter className={cn(
            "h-4 w-4",
            isFiltered && "fill-primary"
          )} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          {/* Header with clear button */}
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Filter by Tags</Label>
            {isFiltered && (
              <Button variant="ghost" size="sm" onClick={clearFilter}>
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            )}
          </div>
          
          {availableTags.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No tags available
            </p>
          ) : (
            <>
              {/* Search input */}
              <Input
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
              />
              
              {/* Tag list */}
              <ScrollArea className="h-64">
                <div className="space-y-2 pr-4">
                  {filteredTags.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No tags found
                    </p>
                  ) : (
                    filteredTags.map(tag => (
                      <div key={tag.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`filter-${tag.id}`}
                          checked={selectedTagIds.includes(tag.id)}
                          onCheckedChange={(checked) => handleTagToggle(tag.id, !!checked)}
                        />
                        <Label
                          htmlFor={`filter-${tag.id}`}
                          className="flex items-center gap-2 flex-1 cursor-pointer font-normal"
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="flex-1">{tag.name}</span>
                          <Badge variant="outline" className="ml-auto text-xs">
                            {tag.type}
                          </Badge>
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
              
              {/* Selection count */}
              <div className="text-xs text-muted-foreground pt-2 border-t">
                {selectedTagIds.length === 0
                  ? "Select tags to filter devices"
                  : `${selectedTagIds.length} tag${selectedTagIds.length > 1 ? 's' : ''} selected`
                }
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
